import { planUploadBatches } from '../../dropped-upload-files';
import { mapWithConcurrency } from '../map-with-concurrency';
import { registerInChunks } from '../register-in-chunks';
import {
  folderUploadDirectoriesToCreate,
  splitFolderUploadDirectory,
} from './empty-directories';
import {
  convexErrorCode,
  folderUploadFailureReason,
  isFinalFolderUploadFailure,
} from './failure-reason';
import {
  cancelAllFolderUploadFiles,
  cancelFolderUploadFiles,
  completeFolderUploadFiles,
  failFolderUploadFiles,
  markFolderUploadFilesRegistering,
  recordFolderUploadEmptyFolders,
  requeueFolderUploadFiles,
  setFolderUploadFileProgress,
  setFolderUploadJobRunning,
  startFolderUploadFile,
} from './reducers';
import type {
  FolderUploadDeps,
  FolderUploadLimits,
  FolderUploadPlan,
  FolderUploadRegisteredFile,
  FolderUploadRegistration,
} from './runner-types';
import type { FolderUploadStore } from './store';
import type { FolderUploadJobState } from './types';

export type FolderUploadRunner = {
  /** Sends every queued file; resolves once the upload stops running. */
  start: () => Promise<void>;
  /** Stops queued batches and aborts transfers; finished files stay. */
  cancelAll: () => void;
  cancelFile: (id: number) => void;
  /** Sends these failed or cancelled files again, into the same folders. */
  retry: (ids: ReadonlyArray<number>) => Promise<void>;
};

type PreparedUpload = {
  id: number;
  directoryPath: string;
  upload: FolderUploadRegistration;
};

function joinDirectory(target: string, relative: string | null): string {
  const base = target.replace(/\/+$/, '');
  return relative ? `${base}/${relative}` : base;
}

/**
 * Runs one folder upload: batches of at most `batchMaxFiles` files and
 * `batchMaxBytes`, `concurrency` transfers at a time. A failed or cancelled
 * file never fails its batch: the rest are filed, and only that file's own
 * reserved key is released.
 */
export function createFolderUploadRunner(args: {
  jobId: string;
  plan: FolderUploadPlan;
  deps: FolderUploadDeps;
  limits: FolderUploadLimits;
  store: FolderUploadStore;
  now?: () => number;
}): FolderUploadRunner {
  const { jobId, plan, deps, limits, store } = args;
  const now = args.now ?? Date.now;
  let pending: number[] = [];
  let root = new AbortController();
  const inFlight = new Map<number, AbortController>();
  let active: Promise<void> | null = null;
  let directoriesCreated = plan.emptyDirectories.length === 0;

  const job = () => store.get(jobId);
  const update = (
    recipe: (job: FolderUploadJobState) => FolderUploadJobState,
    coalesce = false
  ) => store.update(jobId, recipe, { coalesce });
  const release = (storageKey: string) =>
    deps.deleteUpload(storageKey).catch(() => undefined);

  function takeBatch(): number[] {
    const [batch = []] = planUploadBatches(
      pending.map((id) => ({ id, size: plan.files[id]?.file.size ?? 0 })),
      limits.batchMaxFiles,
      limits.batchMaxBytes
    );
    pending = pending.slice(batch.length);
    return batch.map((item) => item.id);
  }

  async function transferOne(
    id: number,
    batchSignal: AbortSignal
  ): Promise<PreparedUpload | null> {
    const entry = plan.files[id];
    if (!entry || job()?.files[id]?.status !== 'queued') return null;
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (batchSignal.aborted) controller.abort();
    else batchSignal.addEventListener('abort', abort, { once: true });
    inFlight.set(id, controller);
    update((current) => startFolderUploadFile(current, id));
    let storageKey: string | null = null;
    try {
      const reservation = controller.signal.aborted
        ? null
        : await deps.mint({
            kind: plan.mintKind,
            filename: entry.name,
            contentType: entry.mimeType,
            mediaType: entry.mediaType,
          });
      storageKey = reservation?.storageKey ?? null;
      if (reservation && !controller.signal.aborted)
        await deps.transfer({
          uploadUrl: reservation.uploadUrl,
          file: entry.file,
          contentType: entry.mimeType,
          signal: controller.signal,
          onProgress: (loaded) =>
            update(
              (current) =>
                setFolderUploadFileProgress(current, id, loaded, now()),
              true
            ),
        });
      // A user cancel is checked here, never inferred from the error: the
      // proxy transport reports an abort as a timeout.
      if (!reservation || controller.signal.aborted)
        throw new DOMException('Upload cancelled', 'AbortError');
      update((current) =>
        markFolderUploadFilesRegistering(current, [id], now())
      );
      return {
        id,
        directoryPath: joinDirectory(
          plan.targetDirectoryPath,
          entry.relativeDirectoryPath
        ),
        upload: {
          storageKey: reservation.storageKey,
          url:
            reservation.bucketKind === 'public'
              ? reservation.publicUrl
              : undefined,
          filename: entry.name,
          mimeType: entry.mimeType,
          sizeBytes: entry.file.size,
          uploadId: Math.random().toString(36).slice(2),
        },
      };
    } catch (error) {
      if (storageKey) await release(storageKey);
      const reason = folderUploadFailureReason(error);
      if (controller.signal.aborted)
        update((current) => cancelFolderUploadFiles(current, [id]));
      else {
        deps.onFileFailed?.({ error, stage: 'transfer', reason });
        update((current) =>
          failFolderUploadFiles(
            current,
            [id],
            reason,
            'transfer',
            isFinalFolderUploadFailure(error, reason)
          )
        );
      }
      return null;
    } finally {
      inFlight.delete(id);
      batchSignal.removeEventListener('abort', abort);
    }
  }

  async function processBatch(ids: number[], signal: AbortSignal) {
    const prepared = await mapWithConcurrency(ids, limits.concurrency, (id) =>
      transferOne(id, signal)
    );
    const groups = new Map<string, PreparedUpload[]>();
    for (const item of prepared) {
      if (!item) continue;
      const group = groups.get(item.directoryPath) ?? [];
      group.push(item);
      groups.set(item.directoryPath, group);
    }
    const registered: Array<{
      directoryPath: string;
      uploads: FolderUploadRegistration[];
      results: ReadonlyArray<FolderUploadRegisteredFile>;
    }> = [];
    // Each folder is filed on its own (keeping the structure) in small
    // retried chunks so a call stays inside the server's time limit (#5918).
    // A file that still is not filed fails alone, with Retry.
    for (const [directoryPath, items] of groups) {
      const { results, failed } = await registerInChunks(
        items,
        async (chunk) => [
          ...(await deps.register({
            targetDirectoryPath: directoryPath,
            uploads: chunk.map((item) => item.upload),
          })),
        ],
        {
          itemKey: (item) => item.upload.storageKey,
          resultKey: (file) => file.r2Key,
          // Not cancelled by Cancel all: bytes already sent are still filed.
          sleep: deps.sleep,
        }
      );
      if (results.length > 0) {
        const filed = new Set(results.map((file) => file.r2Key));
        const done = items.filter((item) => filed.has(item.upload.storageKey));
        update((current) =>
          completeFolderUploadFiles(
            current,
            done.map((item) => item.id)
          )
        );
        registered.push({
          directoryPath,
          uploads: done.map((item) => item.upload),
          results,
        });
      }
      for (const { item, error } of failed) {
        const reason = folderUploadFailureReason(error);
        deps.onFileFailed?.({ error, stage: 'register', reason });
        update((current) =>
          failFolderUploadFiles(
            current,
            [item.id],
            reason,
            'register',
            isFinalFolderUploadFailure(error, reason)
          )
        );
        await release(item.upload.storageKey);
      }
    }
    if (registered.length > 0) deps.onBatchRegistered?.(registered);
  }

  async function createEmptyDirectories() {
    // Marked done only when every empty folder exists, so a transient failure
    // is tried again by Retry (review S1).
    directoriesCreated = true;
    const doneDirectories = (job()?.files ?? [])
      .filter((file) => file.status === 'done')
      .map((file) => file.relativeDirectoryPath);
    const leaves = new Set(plan.emptyDirectories);
    const unavailable = new Set<string>();
    let failed = 0;
    for (const relative of folderUploadDirectoriesToCreate(
      plan.emptyDirectories,
      doneDirectories
    )) {
      // Cancel all stops queued folder creation too; a later retry resumes
      // it (an existing folder counts as created).
      if (root.signal.aborted || job()?.cancelRequested) {
        directoriesCreated = false;
        return;
      }
      const { parent, name } = splitFolderUploadDirectory(relative);
      let created = !(parent && unavailable.has(parent));
      if (created) {
        try {
          await deps.createDirectory({
            parentPath: joinDirectory(plan.targetDirectoryPath, parent),
            name,
          });
        } catch (error) {
          created = convexErrorCode(error) === 'VFS_PATH_ALREADY_EXISTS';
        }
      }
      if (created) continue;
      unavailable.add(relative);
      if (leaves.has(relative)) failed += 1;
    }
    if (failed > 0) directoriesCreated = false;
    // Recorded every pass, so a successful retry clears the note.
    update((current) => recordFolderUploadEmptyFolders(current, failed));
  }

  async function drain() {
    // Lets `run` record this drain before it can finish.
    await null;
    for (;;) {
      while (pending.length > 0) await processBatch(takeBatch(), root.signal);
      if (!directoriesCreated && job()?.cancelRequested === false)
        await createEmptyDirectories();
      if (pending.length === 0) break;
    }
    active = null;
    update((current) => setFolderUploadJobRunning(current, false, now()));
    const finished = job();
    if (finished) deps.onRunEnd?.(finished);
  }

  function run(): Promise<void> {
    if (active) return active;
    update((current) => setFolderUploadJobRunning(current, true, now()));
    active = drain();
    return active;
  }

  return {
    start() {
      const current = job();
      if (!current || current.blockedReason) {
        if (current) deps.onRunEnd?.(current);
        return Promise.resolve();
      }
      pending = current.files
        .filter((file) => file.status === 'queued')
        .map((file) => file.id);
      return run();
    },
    cancelAll() {
      pending = [];
      root.abort();
      // A later retry runs under a fresh signal.
      root = new AbortController();
      update(cancelAllFolderUploadFiles);
    },
    cancelFile(id) {
      pending = pending.filter((pendingId) => pendingId !== id);
      inFlight.get(id)?.abort();
      update((current) => cancelFolderUploadFiles(current, [id]));
    },
    retry(ids) {
      const current = job();
      if (!current || current.blockedReason) return Promise.resolve();
      const eligible = ids.filter((id) => {
        const file = current.files[id];
        return (
          file?.retryable === true &&
          (file.status === 'failed' || file.status === 'cancelled')
        );
      });
      // Empty folders that failed are retried even with no file to resend.
      if (eligible.length === 0 && directoriesCreated)
        return active ?? Promise.resolve();
      update((state) => requeueFolderUploadFiles(state, eligible));
      pending.push(...eligible);
      return run();
    },
  };
}
